import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LightbulbIcon, TargetIcon, FlameIcon, LockIcon, SparklesIcon, TrophyIcon, BrainIcon, BookOpenIcon } from 'lucide-react';
import { DjayAvatar, DjayEmotion } from './DjayAvatar';
import { DjayBubble, BubbleColorScheme } from './DjayBubble';
import { useDjaySelection } from './useDjaySelection';
import { useEasterEggs } from './useEasterEggs';
import { useVariantUnlocks } from './useVariantUnlocks';
import { useLearnerContext } from './useLearnerContext';
import { DJAY_TIMINGS } from './config';
import { createRapidTapDetector, createKonamiDetector, checkTimeBasedEgg, getRapidTapReaction } from './easterEggs';
import { useAppStore } from '../app/hooks/useAppStore';
import { useMotionBudget } from '../app/hooks/useMotionBudget';
import { useClickOutside } from '../app/hooks/useClickOutside';
import { soundManager } from '../utils/soundManager';
import { hapticManager } from '../utils/hapticManager';
import { dataLayer } from '../data/dataLayer';
import { getDjayPersonalGreeting, getDjayViewMessage, getDjayDailyTip, getDjayIdleMessage } from './djayMessages';
import type { LearnerContext } from './djayMessages';
import { useErrorPatterns } from '../components/progress/ErrorPatternsSection';
// ── Types ────────────────────────────────────────────────────
interface FloatingDjayProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
}
type ItemAccessory = 'book' | 'sparkles' | 'none';
interface PanelMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  hoverClass: string;
  textClass: string;
  onClick: () => void;
  /** Hint Djay speaks when this item is hovered/focused — first-person, friendly */
  bubbleHint: string;
  /** Color scheme for the speech bubble when this item is hovered */
  bubbleColor: BubbleColorScheme;
  /** Emotion Djay shows when this item is hovered/focused */
  hoverEmotion: DjayEmotion;
  /** Optional accessory prop that floats next to Djay when this item is focused */
  accessory?: ItemAccessory;
}
// ── Contextual Menu Builder ──────────────────────────────────
function getContextualMenuItems(currentView: string, learnerContext: LearnerContext, errorPatterns: Array<{
  recommendation: string;
  familyName: string;
}>, handlers: {
  onTipClick: () => void;
  onNavigate: (path: string) => void;
  onErrorTipClick: (tip: string) => void;
}): PanelMenuItem[] {
  const {
    streak,
    displayName
  } = learnerContext;
  const items: PanelMenuItem[] = [];
  // If there are error patterns, show a personalized learning tip
  if (errorPatterns.length > 0) {
    const topPattern = errorPatterns[0];
    items.push({
      id: 'error_tip',
      label: '💡 Tip voor jou',
      icon: <LightbulbIcon className="w-4 h-4" data-id="element-3358" />,
      bgClass: 'bg-violet-50',
      hoverClass: 'hover:bg-violet-100',
      textClass: 'text-violet-700',
      onClick: () => handlers.onErrorTipClick(topPattern.recommendation),
      bubbleHint: `Ik zag iets bij ${topPattern.familyName.toLowerCase()} — wil je m'n tip horen? ✨`,
      bubbleColor: 'violet',
      hoverEmotion: 'encouraging',
      accessory: 'sparkles'
    });
  }
  // Always show "Weetje van de dag"
  items.push({
    id: 'weetje',
    label: 'Weetje van de dag',
    icon: <LightbulbIcon className="w-4 h-4" data-id="element-3359" />,
    bgClass: 'bg-amber-50',
    hoverClass: 'hover:bg-amber-100',
    textClass: 'text-amber-700',
    onClick: handlers.onTipClick,
    bubbleHint: 'Ik heb elke dag een nieuw weetje voor je! Klik maar 🤓',
    bubbleColor: 'amber',
    hoverEmotion: 'pointing',
    accessory: 'book'
  });
  switch (currentView) {
    case 'home':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3360" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab'),
        bubbleHint: `Klaar om te trainen, ${displayName}? Ik heb een plan! 💪`,
        bubbleColor: 'default',
        hoverEmotion: 'excited',
        accessory: 'sparkles'
      });
      break;
    case 'subjects':
    case 'modules':
      items.push({
        id: 'voortgang',
        label: 'Bekijk voortgang',
        icon: <BrainIcon className="w-4 h-4" data-id="element-3361" />,
        bgClass: 'bg-emerald-50',
        hoverClass: 'hover:bg-emerald-100',
        textClass: 'text-emerald-700',
        onClick: () => handlers.onNavigate('progress'),
        bubbleHint: 'Kijk hoe ver je al gekomen bent! Ik ben trots! 🌟',
        bubbleColor: 'emerald',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    case 'progress':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3362" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab'),
        bubbleHint: 'Tijd om die voortgang te laten groeien! 🚀',
        bubbleColor: 'default',
        hoverEmotion: 'excited',
        accessory: 'sparkles'
      });
      break;
    case 'collection':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3363" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab'),
        bubbleHint: 'Verdien meer trofeeën — ga oefenen! 🏆',
        bubbleColor: 'default',
        hoverEmotion: 'excited',
        accessory: 'sparkles'
      });
      break;
    case 'learningLab':
    case 'lab':
      items.push({
        id: 'collectie',
        label: 'Mijn collectie',
        icon: <TrophyIcon className="w-4 h-4" data-id="element-3364" />,
        bgClass: 'bg-violet-50',
        hoverClass: 'hover:bg-violet-100',
        textClass: 'text-violet-700',
        onClick: () => handlers.onNavigate('collection'),
        bubbleHint: 'Bekijk alle trofeeën die je verdiend hebt! ✨',
        bubbleColor: 'violet',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    default:
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3365" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab'),
        bubbleHint: 'Kom, we gaan oefenen! 💪',
        bubbleColor: 'default',
        hoverEmotion: 'excited',
        accessory: 'sparkles'
      });
      break;
  }
  if (streak > 0) {
    items.push({
      id: 'streak',
      label: `Mijn streak: ${streak} dagen`,
      icon: <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-3366" />,
      bgClass: 'bg-rose-50',
      hoverClass: 'hover:bg-rose-100',
      textClass: 'text-rose-700',
      onClick: () => handlers.onNavigate('progress'),
      bubbleHint: streak >= 7 ? `${streak} dagen op rij?! Je bent een legende! 🔥👑` : `${streak} dagen streak — ga zo door! 🔥`,
      bubbleColor: 'amber',
      hoverEmotion: streak >= 7 ? 'celebrating' : 'excited',
      accessory: 'sparkles'
    });
  }
  return items;
}
// ── Emotion → Secondary Motion Map ───────────────────────────
// Adds liveliness on top of the avatar's own animation.
function getEmotionMotion(emotion: DjayEmotion, shouldAnimate: boolean) {
  if (!shouldAnimate) return undefined;
  switch (emotion) {
    case 'excited':
      return {
        rotate: [0, -6, 6, -4, 4, 0],
        transition: {
          duration: 0.8,
          ease: 'easeInOut' as const
        }
      };
    case 'celebrating':
      return {
        rotate: [0, -10, 10, -8, 8, 0],
        scale: [1, 1.08, 1],
        transition: {
          duration: 0.9,
          ease: 'easeInOut' as const
        }
      };
    case 'proud':
      return {
        scale: [1, 1.1, 1],
        y: [0, -3, 0],
        transition: {
          duration: 0.7,
          ease: 'easeOut' as const
        }
      };
    case 'thinking':
      return {
        rotate: [0, -4, -2, -4, 0],
        transition: {
          duration: 2.4,
          ease: 'easeInOut' as const,
          repeat: Infinity
        }
      };
    case 'pointing':
      return {
        x: [0, -3, 0],
        rotate: [0, -3, 0],
        transition: {
          duration: 0.6,
          ease: 'easeOut' as const
        }
      };
    case 'surprised':
      return {
        scale: [1, 1.15, 0.95, 1],
        transition: {
          duration: 0.5,
          ease: 'easeOut' as const
        }
      };
    case 'encouraging':
      return {
        y: [0, -4, 0, -2, 0],
        transition: {
          duration: 0.8,
          ease: 'easeInOut' as const
        }
      };
    case 'sleeping':
      return {
        rotate: [0, -2, 0],
        transition: {
          duration: 3,
          ease: 'easeInOut' as const,
          repeat: Infinity
        }
      };
    default:
      return undefined;
  }
}
// ── Floating Book Accessory ──────────────────────────────────
function FloatingBook({
  shouldAnimate
}: {
  shouldAnimate: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.5,
    x: 16,
    y: 8,
    rotate: -30
  }} animate={{
    opacity: 1,
    scale: 1,
    x: 0,
    y: 0,
    rotate: -12
  }} exit={{
    opacity: 0,
    scale: 0.5,
    x: 16,
    y: 8,
    rotate: -30
  }} transition={{
    type: 'spring',
    damping: 18,
    stiffness: 260
  }} className="absolute -left-8 bottom-2 z-0 pointer-events-none" aria-hidden="true" data-id="element-3367">
      <motion.div animate={shouldAnimate ? {
      y: [0, -3, 0],
      rotate: [-12, -8, -12]
    } : undefined} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="w-9 h-9 bg-gradient-to-br from-amber-100 to-amber-200 rounded-md border-2 border-amber-400 shadow-lg flex items-center justify-center" style={{
      willChange: 'transform'
    }} data-id="element-3368">
        <BookOpenIcon className="w-5 h-5 text-amber-700 stroke-[2.5]" data-id="element-3369" />
      </motion.div>
    </motion.div>;
}
// ── Floating Sparkles Accessory ──────────────────────────────
function FloatingSparkles({
  shouldAnimate
}: {
  shouldAnimate: boolean;
}) {
  if (!shouldAnimate) return null;
  const positions = [{
    x: -14,
    y: -8,
    delay: 0,
    size: 'w-3 h-3'
  }, {
    x: 18,
    y: -14,
    delay: 0.3,
    size: 'w-2.5 h-2.5'
  }, {
    x: -10,
    y: 14,
    delay: 0.6,
    size: 'w-2 h-2'
  }];
  return <div className="absolute inset-0 pointer-events-none z-20" aria-hidden="true" data-id="element-3370">
      {positions.map((p, i) => <motion.div key={i} className={`absolute ${p.size} text-amber-400`} style={{
      left: '50%',
      top: '50%',
      x: p.x,
      y: p.y
    }} initial={{
      opacity: 0,
      scale: 0,
      rotate: 0
    }} animate={{
      opacity: [0, 1, 0],
      scale: [0, 1.2, 0],
      rotate: [0, 180]
    }} exit={{
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 1.4,
      repeat: Infinity,
      delay: p.delay,
      ease: 'easeInOut'
    }} data-id="element-3371">
          <SparklesIcon className="w-full h-full fill-amber-300" data-id="element-3372" />
        </motion.div>)}
    </div>;
}
export function FloatingDjay({
  currentView,
  onNavigate
}: FloatingDjayProps) {
  const {
    activeAccount,
    isDemoMode
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const {
    triggerEgg
  } = useEasterEggs();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  const [showEasterEggLabel, setShowEasterEggLabel] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState<string | null>(null);
  const [bubbleColor, setBubbleColor] = useState<BubbleColorScheme>('default');
  const [bubbleAutoDismiss, setBubbleAutoDismiss] = useState<number | undefined>(undefined);
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isPulsing, setIsPulsing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLongPressTriggered = useRef(false);
  const hasShownWelcomeRef = useRef(false);
  const welcomeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const easterEggTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastViewRef = useRef<string | undefined>(currentView);
  const showProactiveMessageRef = useRef<(msg: string, newEmotion: DjayEmotion, duration: number) => void>(() => {});
  const rapidTapRef = useRef(createRapidTapDetector(DJAY_TIMINGS.RAPID_TAP_THRESHOLD, DJAY_TIMINGS.RAPID_TAP_WINDOW_MS, DJAY_TIMINGS.RAPID_TAP_COOLDOWN_MS));
  const konamiRef = useRef(createKonamiDetector());
  const timeEggShownRef = useRef(false);
  // Shared learner aggregate — replaces previous in-component useMemo
  // that duplicated the iteration done by useVariantUnlocks (audit DDD-3).
  const learnerContext = useLearnerContext();
  const {
    displayName,
    streak
  } = learnerContext;
  // Ref to latest learnerContext so idle-effect doesn't need to re-attach
  // global listeners on every streak/XP mutation (audit H3).
  const learnerContextRef = useRef(learnerContext);
  useEffect(() => {
    learnerContextRef.current = learnerContext;
  }, [learnerContext]);
  useEffect(() => {
    if (!activeAccount?.id) return;
    let cancelled = false;
    dataLayer.getDjayLongPressDiscovered(activeAccount.id).then(val => {
      if (!cancelled) setHasDiscoveredLongPress(val);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeAccount?.id]);
  const {
    variants: unlockedVariants
  } = useVariantUnlocks(hasDiscoveredLongPress);
  useClickOutside(panelRef, () => {
    if (isPanelOpen) {
      setIsPanelOpen(false);
      setHoveredItemId(null);
      setBubbleMessage(null);
    }
    if (isLongPressMenuOpen) setIsLongPressMenuOpen(false);
  });
  // Detect touch-only devices (no hover) — needed because per-item hover
  // bubbles never fire on tablets/phones. We auto-cycle them instead.
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(hover: none)');
    const update = () => setIsTouchDevice(mql.matches);
    update();
    mql.addEventListener?.('change', update);
    return () => mql.removeEventListener?.('change', update);
  }, []);
  // Ref holding the latest menuItems so the auto-cycle effect doesn't
  // need to be torn down whenever the menu items recompute.
  const menuItemsRef = useRef<PanelMenuItem[]>([]);
  const state = useAppStore();
  const selectedGroup = state.activeAccount?.group || 6;
  const errorPatterns = useErrorPatterns(state, selectedGroup);
  // Touch auto-cycle: when the panel is open on a no-hover device,
  // step through the menu items every 2.5s so each item's speech
  // bubble is shown in turn (the "guided djay" effect on mobile).
  useEffect(() => {
    if (!isTouchDevice || !isPanelOpen) return;
    let index = 0;
    const tick = () => {
      const items = menuItemsRef.current;
      if (items.length === 0) return;
      const item = items[index % items.length];
      index += 1;
      setHoveredItemId(item.id);
      setBubbleMessage(item.bubbleHint);
      setBubbleColor(item.bubbleColor);
      setBubbleAutoDismiss(0);
      setEmotion(item.hoverEmotion);
    };
    const startTimer = setTimeout(tick, DJAY_TIMINGS.TOUCH_CYCLE_INITIAL_MS);
    const interval = setInterval(tick, DJAY_TIMINGS.TOUCH_CYCLE_INTERVAL_MS);
    return () => {
      clearTimeout(startTimer);
      clearInterval(interval);
    };
  }, [isTouchDevice, isPanelOpen]);
  const showProactiveMessage = (msg: string, newEmotion: DjayEmotion, duration: number) => {
    if (isPanelOpen || isLongPressMenuOpen) return;
    setIsPulsing(true);
    setEmotion(newEmotion);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      setBubbleMessage(msg);
      setBubbleColor('default');
      setBubbleAutoDismiss(duration);
      soundManager.playDjaySpeak();
    }, DJAY_TIMINGS.ATTENTION_PULSE_MS);
  };
  showProactiveMessageRef.current = showProactiveMessage;
  useEffect(() => {
    if (!hasShownWelcomeRef.current && activeAccount) {
      hasShownWelcomeRef.current = true;
      const greeting = getDjayPersonalGreeting(learnerContext);
      welcomeTimerRef.current = setTimeout(() => {
        showProactiveMessageRef.current(greeting.text, greeting.emotion, DJAY_TIMINGS.WELCOME_BUBBLE_MS);
      }, DJAY_TIMINGS.WELCOME_DELAY_MS);
    }
    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, [activeAccount, learnerContext]);
  useEffect(() => {
    if (currentView && currentView !== lastViewRef.current && currentView !== 'practice') {
      lastViewRef.current = currentView;
      const viewMsg = getDjayViewMessage(currentView, {
        streak,
        displayName
      });
      showProactiveMessage(viewMsg.text, viewMsg.emotion, DJAY_TIMINGS.VIEW_CHANGE_BUBBLE_MS);
    }
  }, [currentView, streak, displayName]);
  // ── Just-in-time error tip surfacing on Voortgang page ─────
  // When the learner opens the progress view AND has at least one
  // recurring error pattern AND we haven't already shown a tip today,
  // Djay proactively offers a tip after the welcome bubble settles.
  useEffect(() => {
    if (currentView !== 'progress') return;
    if (errorPatterns.length === 0) return;
    if (!activeAccount?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    const storageKey = `djay_last_error_tip_${activeAccount.id}`;
    try {
      const lastShown = window.localStorage.getItem(storageKey);
      if (lastShown === today) return;
    } catch {
      // ignore storage errors — still show the tip
    }
    const topPattern = errorPatterns[0];
    const tipText = `Ik zag iets bij ${topPattern.familyName.toLowerCase()}: ${topPattern.recommendation}`;
    const timer = setTimeout(() => {
      showProactiveMessageRef.current(tipText, 'encouraging', 9000);
      try {
        window.localStorage.setItem(storageKey, today);
      } catch {
        // ignore
      }
    }, DJAY_TIMINGS.VIEW_CHANGE_BUBBLE_MS + 800);
    return () => clearTimeout(timer);
  }, [currentView, errorPatterns, activeAccount?.id]);
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        const idleMsg = getDjayIdleMessage(learnerContextRef.current);
        showProactiveMessageRef.current(idleMsg.text, idleMsg.emotion, DJAY_TIMINGS.IDLE_BUBBLE_MS);
      }, DJAY_TIMINGS.IDLE_TIMEOUT_MS);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
    };
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (konamiRef.current.handleKey(e.key)) {
        const result = triggerEgg('konami-code');
        if (result.triggered && result.reaction) {
          setEmotion(result.reaction.emotion);
          setBubbleMessage(result.reaction.text);
          setBubbleColor('default');
          setBubbleAutoDismiss(DJAY_TIMINGS.KONAMI_BUBBLE_MS);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerEgg]);
  useEffect(() => {
    if (timeEggShownRef.current || !activeAccount) return;
    const timeEgg = checkTimeBasedEgg();
    if (timeEgg) {
      timeEggShownRef.current = true;
      const timer = setTimeout(() => {
        const result = triggerEgg(timeEgg);
        if (result.triggered && result.reaction) {
          showProactiveMessageRef.current(result.reaction.text, result.reaction.emotion, DJAY_TIMINGS.TIME_EGG_BUBBLE_MS);
        }
      }, DJAY_TIMINGS.TIME_EGG_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [activeAccount, triggerEgg]);
  const handlePointerDown = () => {
    isLongPressTriggered.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsPanelOpen(false);
      setHoveredItemId(null);
      setIsLongPressMenuOpen(true);
      if (!hasDiscoveredLongPress && activeAccount?.id) {
        setHasDiscoveredLongPress(true);
        dataLayer.saveDjayLongPressDiscovered(activeAccount.id, true);
        triggerEgg('long-press-goud');
        setShowEasterEggLabel(true);
        if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
        easterEggTimerRef.current = setTimeout(() => setShowEasterEggLabel(false), DJAY_TIMINGS.EASTER_EGG_LABEL_MS);
        setEmotion('celebrating');
      } else {
        setEmotion('excited');
        soundManager.playDjaySpeak();
        hapticManager.hapticHeavy();
      }
    }, DJAY_TIMINGS.LONG_PRESS_MS);
  };
  const handlePointerUp = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  };
  const handleDjayClick = () => {
    if (isLongPressTriggered.current) return;
    setTapPulse(true);
    setTimeout(() => setTapPulse(false), DJAY_TIMINGS.TAP_PULSE_MS);
    const tapResult = rapidTapRef.current.recordTap();
    if (tapResult.inCooldown) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      return;
    }
    if (tapResult.triggered) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      const reaction = getRapidTapReaction(tapResult.triggerCount);
      triggerEgg('rapid-tap');
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(false);
      setHoveredItemId(null);
      setEmotion(reaction.emotion);
      setBubbleMessage(reaction.text);
      setBubbleColor('default');
      setBubbleAutoDismiss(DJAY_TIMINGS.EASTER_EGG_BUBBLE_MS);
      setShowEasterEggLabel(false);
      soundManager.playDjaySpeak();
      hapticManager.hapticCelebration();
      return;
    }
    if (isLongPressMenuOpen) {
      setIsLongPressMenuOpen(false);
      return;
    }
    if (isPanelOpen) {
      setIsPanelOpen(false);
      setHoveredItemId(null);
      setBubbleMessage(null);
      return;
    }
    setIsPulsing(false);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    const viewMsg = getDjayViewMessage(currentView || 'home', {
      streak,
      displayName
    });
    setEmotion(viewMsg.emotion);
    setIsPanelOpen(true);
    // Show greeting bubble when menu opens — Djay introduces himself
    setBubbleMessage(viewMsg.text);
    setBubbleColor('default');
    setBubbleAutoDismiss(0); // 0 disables auto-dismiss; persists until menu closes
    soundManager.playDjaySpeak();
  };
  const handleTipClick = () => {
    const tip = getDjayDailyTip();
    setEmotion(tip.emotion);
    setIsPanelOpen(false);
    setHoveredItemId(null);
    setBubbleMessage(tip.text);
    setBubbleColor('amber');
    setBubbleAutoDismiss(DJAY_TIMINGS.TIP_BUBBLE_MS);
    soundManager.playDjaySpeak();
  };
  const handleErrorTipClick = (tip: string) => {
    setEmotion('encouraging');
    setIsPanelOpen(false);
    setHoveredItemId(null);
    setBubbleMessage(tip);
    setBubbleColor('violet');
    setBubbleAutoDismiss(DJAY_TIMINGS.TIP_BUBBLE_MS);
    soundManager.playDjaySpeak();
  };
  const handleNavClick = (path: string) => {
    setIsLongPressMenuOpen(false);
    setIsPanelOpen(false);
    setHoveredItemId(null);
    setBubbleMessage(null);
    if (onNavigate) onNavigate(path);
  };
  if (currentView === 'practice' || !activeAccount && !isDemoMode) {
    return null;
  }
  const greetingMsg = getDjayViewMessage(currentView || 'home', {
    streak,
    displayName
  });
  const menuItems = getContextualMenuItems(currentView || 'home', learnerContext, errorPatterns, {
    onTipClick: handleTipClick,
    onNavigate: handleNavClick,
    onErrorTipClick: handleErrorTipClick
  });
  // Sync menuItems into ref via effect (audit H2 — was previously
  // assigned in render body which is a React anti-pattern).
  useEffect(() => {
    menuItemsRef.current = menuItems;
  }, [menuItems]);
  // Hover/focus on a menu item → swap bubble + emotion + accessory
  const handleItemHover = (item: PanelMenuItem) => {
    setHoveredItemId(item.id);
    setBubbleMessage(item.bubbleHint);
    setBubbleColor(item.bubbleColor);
    setBubbleAutoDismiss(0); // persists while user keeps focus on this item
    setEmotion(item.hoverEmotion);
    soundManager.playDjaySpeak();
  };
  const handleItemLeave = () => {
    setHoveredItemId(null);
    // Return to greeting
    setBubbleMessage(greetingMsg.text);
    setBubbleColor('default');
    setEmotion(greetingMsg.emotion);
  };
  // Determine which accessory to show
  const hoveredItem = hoveredItemId ? menuItems.find(m => m.id === hoveredItemId) : null;
  const showBook = isPanelOpen && hoveredItem?.accessory === 'book';
  const showSparkles = isPanelOpen && hoveredItem?.accessory === 'sparkles' || emotion === 'celebrating';
  const emotionMotion = getEmotionMotion(emotion, shouldAnimate);
  const isBubbleVisible = !!bubbleMessage && !isLongPressMenuOpen && !showEasterEggLabel;
  return <motion.div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-30 flex items-end justify-end pointer-events-none" initial={shouldAnimate ? {
    y: 100,
    opacity: 0
  } : false} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} data-id="element-3373">
      <div className="relative flex items-end pointer-events-auto" ref={panelRef} data-id="element-3374">
        {/* Expanded Click Panel — Dynamic Menu */}
        <AnimatePresence data-id="element-3375">
          {isPanelOpen && !isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.2
        }} className="absolute bottom-full right-0 mb-4 w-64 md:w-72 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 origin-bottom-right" data-id="element-3376">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-3377" />

              <div className="relative z-10" data-id="element-3378">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 px-2" data-id="element-3379">
                  Wat zullen we doen?
                </p>
                <div className="space-y-1.5" data-id="element-3380">
                  {menuItems.map((item, i) => {
                const isHovered = hoveredItemId === item.id;
                return <motion.button key={item.id} initial={shouldAnimate ? {
                  opacity: 0,
                  x: 12
                } : false} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.05,
                  duration: 0.25,
                  ease: [0.32, 0.72, 0, 1]
                }} onClick={item.onClick} onMouseEnter={() => handleItemHover(item)} onMouseLeave={handleItemLeave} onFocus={() => handleItemHover(item)} onBlur={handleItemLeave} whileHover={shouldAnimate ? {
                  x: 2
                } : undefined} whileTap={shouldAnimate ? {
                  scale: 0.97
                } : undefined} className={`w-full flex items-center gap-2 px-3 py-2.5 ${item.bgClass} ${item.hoverClass} ${item.textClass} rounded-xl text-sm font-semibold transition-all relative ${isHovered ? 'ring-2 ring-offset-1 ring-current/30 shadow-sm' : ''}`} data-id="element-3381">
                        <motion.span animate={isHovered && shouldAnimate ? {
                    rotate: [0, -10, 10, 0],
                    scale: [1, 1.15, 1]
                  } : undefined} transition={{
                    duration: 0.4
                  }} className="flex-shrink-0" data-id="element-3382">
                          {item.icon}
                        </motion.span>
                        <span className="flex-1 text-left" data-id="element-3383">{item.label}</span>
                        {isHovered && shouldAnimate && <motion.span initial={{
                    opacity: 0,
                    x: -4
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} className="text-current/60" aria-hidden="true" data-id="element-3384">
                            →
                          </motion.span>}
                      </motion.button>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Long Press Quick Nav Menu */}
        <AnimatePresence data-id="element-3385">
          {isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.15,
          ease: [0.32, 0.72, 0, 1]
        }} className="absolute bottom-full right-0 mb-4 w-[280px] md:w-[340px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right pointer-events-auto" data-id="element-3386">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-3387" />

              <div className="relative z-10" data-id="element-3388">
                <p className="text-xs text-slate-400 font-medium mb-3" data-id="element-3389">
                  Kies je stijl
                </p>

                <AnimatePresence data-id="element-3390">
                  {showEasterEggLabel && <motion.div initial={{
                opacity: 0,
                scale: 0.8,
                y: -10
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.8,
                y: -10
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="mb-4 relative overflow-hidden rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 p-4" data-id="element-3391">
                      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/40 to-transparent" animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-3392" />
                      <div className="relative z-10 flex items-center gap-3" data-id="element-3393">
                        <motion.div animate={{
                    rotate: [0, 15, -15, 0],
                    scale: [1, 1.2, 1]
                  }} transition={{
                    duration: 0.6,
                    repeat: 2
                  }} data-id="element-3394">
                          <SparklesIcon className="w-8 h-8 text-yellow-500" data-id="element-3395" />
                        </motion.div>
                        <div data-id="element-3396">
                          <p className="font-black text-amber-900 text-base" data-id="element-3397">
                            🏆 Geheim ontdekt!
                          </p>
                          <p className="text-sm font-bold text-amber-700" data-id="element-3398">
                            DJ Goud is ontgrendeld!
                          </p>
                        </div>
                      </div>
                      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" style={{
                  left: `${15 + i * 15}%`,
                  top: `${20 + i % 3 * 25}%`
                }} animate={{
                  y: [0, -12, 0],
                  opacity: [0.3, 1, 0.3],
                  scale: [0.8, 1.2, 0.8]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2
                }} data-id="element-3399" />)}
                    </motion.div>}
                </AnimatePresence>

                <div className="grid grid-cols-4 gap-2" data-id="element-3400">
                  {unlockedVariants.map(v => {
                const isSelected = selectedVariant === v.id;
                const isNewGold = showEasterEggLabel && v.id === 'goud';
                return <button key={v.id} onClick={() => {
                  if (v.unlocked) {
                    setSelectedVariant(v.id);
                    setIsLongPressMenuOpen(false);
                  }
                }} disabled={!v.unlocked} className="flex flex-col items-center gap-1.5 group focus:outline-none" title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-3401">
                        <motion.div animate={isNewGold ? {
                    scale: [1, 1.15, 1]
                  } : {}} transition={isNewGold ? {
                    duration: 1,
                    repeat: Infinity
                  } : {}} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 relative
                            ${isNewGold ? 'ring-3 ring-yellow-400 bg-yellow-50 shadow-lg shadow-yellow-200/50' : ''}
                            ${isSelected && !isNewGold ? 'ring-2 ring-orange-400 scale-105 bg-orange-50 shadow-sm' : ''}
                            ${!isSelected && !isNewGold ? 'bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-slate-100' : ''}
                            ${!v.unlocked ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}
                          `} data-id="element-3402">
                          <div className="w-10 h-10" data-id="element-3403">
                            <DjayAvatar variant={v.id} emotion="happy" size="sm" data-id="element-3404" />
                          </div>
                          {!v.unlocked && <div className="absolute inset-0 bg-slate-900/5 rounded-full flex items-center justify-center backdrop-blur-[0.5px]" data-id="element-3405">
                              <LockIcon className="w-4 h-4 text-slate-500 drop-shadow-sm" data-id="element-3406" />
                            </div>}
                        </motion.div>
                        <span className={`text-[10px] font-medium transition-colors leading-tight text-center
                            ${isNewGold ? 'text-yellow-600 font-bold' : ''}
                            ${isSelected && !isNewGold ? 'text-orange-600' : ''}
                            ${!isSelected && !isNewGold ? 'text-slate-500' : ''}
                          `} data-id="element-3407">
                          {v.name}
                        </span>
                      </button>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Speech Bubble — switches per hovered menu item */}
        <div className="absolute bottom-full right-0 mb-2 origin-bottom-right" data-id="element-3408">
          <DjayBubble message={bubbleMessage || ''} isVisible={isBubbleVisible} onDismiss={() => {
          if (!isPanelOpen) setBubbleMessage(null);
        }} autoDismissMs={bubbleAutoDismiss} position="bottom" colorScheme={bubbleColor} data-id="element-3409" />
        </div>

        {/* Avatar Button */}
        <motion.button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onClick={handleDjayClick} className="relative focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300/50 rounded-full select-none" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.9,
        y: 4
      } : undefined} animate={shouldAnimate && !isPanelOpen && !isLongPressMenuOpen && !bubbleMessage ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform',
        touchAction: 'none'
      }} data-id="element-3410">
          {/* Tap Pulse */}
          <AnimatePresence data-id="element-3411">
            {tapPulse && <motion.div initial={{
            opacity: 0.8,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 border-4 border-orange-300 rounded-full" data-id="element-3412" />}
          </AnimatePresence>

          {/* Attention Pulse Glow */}
          <AnimatePresence data-id="element-3413">
            {isPulsing && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.2, 1]
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 bg-orange-400 rounded-full blur-md" data-id="element-3414" />}
          </AnimatePresence>

          {/* Floating book accessory */}
          <AnimatePresence data-id="element-3415">
            {showBook && <FloatingBook shouldAnimate={shouldAnimate} data-id="element-3416" />}
          </AnimatePresence>

          {/* Floating sparkles */}
          <AnimatePresence data-id="element-3417">
            {showSparkles && <FloatingSparkles shouldAnimate={shouldAnimate} data-id="element-3418" />}
          </AnimatePresence>

          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-3419" />

          {/* Emotion-driven secondary motion wrapper. key={emotion} retriggers cleanly. */}
          <motion.div key={`emotion-${emotion}`} animate={emotionMotion} className="w-12 h-12 md:w-16 md:h-16 relative z-10" style={{
          willChange: 'transform'
        }} data-id="element-3420">
            <DjayAvatar emotion={emotion} variant={selectedVariant} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-3421" />
          </motion.div>
        </motion.button>
      </div>
    </motion.div>;
}